import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../App';
import { Target, Loader2, Send, X, MessageCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';

const AdminWeeklyIntentions = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyingUserId, setReplyingUserId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [replyAlsoEmail, setReplyAlsoEmail] = useState(false);
  const [sending, setSending] = useState(false);

  const fetchIntentions = useCallback(async () => {
    try {
      const res = await apiClient.get('/admin/weekly-intentions/current-week');
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch weekly intentions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIntentions();
  }, [fetchIntentions]);

  const startReply = (intent) => {
    const firstName = (intent.user_name || 'there').split(' ')[0];
    setReplyingUserId(intent.user_id);
    setReplySubject(`I see you, ${firstName} 👊`);
    setReplyText(
      `Love this intention — "${intent.intention}". I'm cheering you on. Anything I can do to help you nail it this week? Reply here or in the app any time.`
    );
    setReplyAlsoEmail(false);
  };

  const cancelReply = () => {
    setReplyingUserId(null);
    setReplyText('');
    setReplySubject('');
    setReplyAlsoEmail(false);
  };

  const sendReply = async () => {
    if (!replyText.trim() || !replySubject.trim()) {
      toast.error('Give it a subject and a message.');
      return;
    }
    setSending(true);
    try {
      await apiClient.post('/admin/notifications/send', {
        title: replySubject.trim(),
        message: replyText.trim(),
        user_ids: [replyingUserId],
        send_email: replyAlsoEmail,
      });
      toast.success(replyAlsoEmail ? 'Message sent (in-app + email).' : 'Message sent.');
      cancelReply();
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatRelative = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-8" data-testid="admin-weekly-intentions">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Target className="text-[#EFB342]" size={22} />
          <h2 className="font-heading text-xl font-bold text-stone-900">THIS WEEK&apos;S INTENTIONS</h2>
        </div>
        {data && (
          <span className="text-xs text-stone-500">
            {data.total} member{data.total === 1 ? '' : 's'} · week of {data.week_start}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="animate-spin text-[#EFB342]" size={24} />
        </div>
      ) : !data?.intentions?.length ? (
        <div className="text-center py-8 text-stone-500">
          <Target size={32} className="mx-auto text-stone-400 mb-2" />
          <p className="text-sm">No intentions set yet this week. Nudge your members on Monday!</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
          {data.intentions.map((intent) => (
            <div
              key={intent.user_id}
              className="bg-stone-50/40 border border-stone-200 rounded-xl p-4 hover:border-[#EFB342]/30 transition-colors"
              data-testid={`admin-intention-row-${intent.user_id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <p className="text-stone-900 font-semibold text-sm">{intent.user_name}</p>
                    <span className="text-stone-400 text-xs">·</span>
                    <p className="text-stone-500 text-xs truncate">{intent.email}</p>
                    <span className="text-stone-400 text-xs">·</span>
                    <p className="text-stone-500 text-xs">{formatRelative(intent.updated_at)}</p>
                  </div>
                  <p className="text-stone-800 text-sm leading-relaxed">
                    &ldquo;{intent.intention}&rdquo;
                  </p>
                </div>
                {replyingUserId !== intent.user_id && (
                  <button
                    type="button"
                    onClick={() => startReply(intent)}
                    className="shrink-0 text-xs font-medium text-[#EFB342] hover:text-[#D99F30] px-3 py-1.5 rounded-lg border border-[#EFB342]/30 hover:bg-[#EFB342]/10 transition-colors flex items-center gap-1.5"
                    data-testid={`admin-intention-reply-btn-${intent.user_id}`}
                  >
                    <MessageCircle size={13} /> Reply
                  </button>
                )}
              </div>

              {replyingUserId === intent.user_id && (
                <div className="mt-3 pt-3 border-t border-stone-200 space-y-2" data-testid={`admin-intention-reply-form-${intent.user_id}`}>
                  <input
                    value={replySubject}
                    onChange={(e) => setReplySubject(e.target.value.slice(0, 100))}
                    placeholder="Subject"
                    className="w-full bg-white/95 border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-900 placeholder:text-stone-500 focus:outline-none focus:border-[#EFB342]/60"
                    data-testid="admin-intention-reply-subject"
                  />
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value.slice(0, 500))}
                    rows={3}
                    placeholder="Message"
                    className="w-full bg-white/95 border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-900 placeholder:text-stone-500 focus:outline-none focus:border-[#EFB342]/60 resize-none"
                    data-testid="admin-intention-reply-message"
                  />
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <label className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer select-none" data-testid={`admin-intention-reply-email-toggle-${intent.user_id}`}>
                      <input
                        type="checkbox"
                        checked={replyAlsoEmail}
                        onChange={(e) => setReplyAlsoEmail(e.target.checked)}
                        className="w-4 h-4 accent-[#EFB342]"
                      />
                      <Mail size={13} className="text-stone-500" />
                      Also send by email
                    </label>
                    <span className="text-xs text-stone-500">{replyText.length}/500</span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={cancelReply}
                        className="text-stone-600 hover:text-stone-900 text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5"
                        data-testid="admin-intention-reply-cancel"
                      >
                        <X size={14} /> Cancel
                      </button>
                      <button
                        type="button"
                        onClick={sendReply}
                        disabled={sending || !replyText.trim() || !replySubject.trim()}
                        className="bg-[#EFB342] text-black hover:bg-[#D99F30] disabled:opacity-60 text-sm font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
                        data-testid="admin-intention-reply-send"
                      >
                        {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                        Send
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminWeeklyIntentions;
